/**
 * Embed & Integrate: the install snippet and the WordPress plugin.
 *
 * The page is explicit about the origin allow-list, because an empty list is
 * the one configuration that looks fine and is not: the widget works, and so
 * does anybody else's copy of it on any other domain.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api/client';
import type { EmbedInstructions } from '../api/types';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  PageHeader,
  Spinner,
} from '../components/ui';
import { useBots } from '../hooks/useBots';
import { botLabel } from '../lib/format';

export function Embed(): JSX.Element {
  const { selected } = useBots();
  const slug = selected?.slug ?? null;

  const [data, setData] = useState<EmbedInstructions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      setData(await api.embedInstructions(slug));
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  async function copy(): Promise<void> {
    if (!data) return;
    await navigator.clipboard.writeText(data.script_snippet);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }

  if (!slug) return <EmptyState title="No bot selected" />;
  if (loading || !data) return <Spinner label="Loading…" />;

  return (
    <div>
      <PageHeader
        title="Embed & Integrate"
        description={`Install ${selected ? botLabel(selected) : ''} on your sites`}
      />

      {error && <ErrorNotice message={error} />}

      {!data.origins_locked && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong>This bot has no origin allow-list.</strong> Any website can currently
          embed it and run up your model costs. Add your domains under{' '}
          <Link to="/settings" className="underline">
            Settings → Identity
          </Link>{' '}
          before going live.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <h2 className="text-base font-semibold">Script tag</h2>
          <p className="mt-1 text-sm text-slate-500">
            Paste this just before <code>&lt;/body&gt;</code> on every page that should
            show the assistant.
          </p>
          <pre className="mt-3 overflow-x-auto rounded bg-slate-900 p-3 text-xs text-slate-100">
            {data.script_snippet}
          </pre>
          <div className="mt-3">
            <Button onClick={() => void copy()}>
              {copied ? 'Copied' : 'Copy snippet'}
            </Button>
          </div>

          <h3 className="mt-5 text-sm font-semibold text-slate-900">Allowed origins</h3>
          {data.allowed_origins.length === 0 ? (
            <p className="mt-1 text-sm text-slate-500">
              None configured — every origin is currently accepted.
            </p>
          ) : (
            <ul className="mt-1 space-y-0.5 text-sm text-slate-600">
              {data.allowed_origins.map((origin) => (
                <li key={origin}>
                  <code>{origin}</code>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="space-y-4">
          <Card className="p-5">
            <h2 className="text-base font-semibold">WordPress</h2>
            <p className="mt-1 text-sm text-slate-500">
              Downloads the plugin already configured with this deployment&apos;s URL
              and the <code>{data.slug}</code> bot, so there is nothing to type in.
              Install it through Plugins → Add New → Upload Plugin.
            </p>
            <div className="mt-3">
              <Button onClick={() => void api.downloadWordPressPlugin(slug)}>
                Download WP plugin
              </Button>
            </div>
          </Card>

          <Card className="p-5">
            <h2 className="text-base font-semibold">Other channels</h2>
            <ul className="mt-2 space-y-2 text-sm text-slate-500">
              {[
                ['Shopify', 'Add the script tag to theme.liquid.'],
                ['HubSpot', 'Add the script tag under Settings → Website → Pages.'],
                ['Slack', 'Not yet available.'],
                ['WhatsApp', 'Not yet available.'],
              ].map(([name, hint]) => (
                <li key={name} className="flex items-baseline justify-between gap-3">
                  <span className="font-medium text-slate-700">{name}</span>
                  <span className="text-right text-xs">{hint}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
